import { FormActions } from "../../components/common/FormActions";
import FormFields from "../../components/common/FormFields";
import {
  createColumn,
  getRegister,
  formatDateTime,
  createViewAction,
  formatPaymentStatus,
  downloadThroughAnchorLink,
  formatAmount,
  numberFormat,
} from "../../utils/helpers";
import { URL_BILLING_INVOICES } from "../../utils/urls";
import { Link } from "react-router-dom";
import React, { useEffect } from "react";
import { API_INVOICES } from "../../utils/apis";
import useGet from "../common/useGet";
import toast from "react-hot-toast";
import { Button, Tooltip } from "@mui/material";
import { ReloadOutline } from "@raresail/react-ionicons";
import { BASE_MEDIA_API_URL } from "../../config";
import { PrintOutlined } from "@mui/icons-material";
import { message } from "antd";
import styled from "styled-components";
import { BASE_API_URL } from "../../config";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";

// Helper function to get status color class
const getStatusColorClass = (status) => {
  const colorMap = {
    paid: "text-green-600 bg-green-100",
    not_paid: "text-red-600 bg-red-100",
    partially_paid: "text-yellow-600 bg-yellow-100",
    overdue: "text-red-800 bg-red-200",
  };
  return colorMap[status] || "text-gray-600 bg-gray-100";
};

// Helper function to format time spent
const formatTimeSpent = (timeSpent) => {
  if (!timeSpent || timeSpent === "00:00:00") return "N/A";

  const parts = timeSpent.split(":");
  const hours = parseInt(parts[0]);
  const minutes = parseInt(parts[1]);
  const seconds = parseInt(parts[2]);

  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  } else {
    return `${seconds}s`;
  }
};

const RegenerateDocuments = ({ item }: { item: any }) => {
  const { loadData: regenerateDocuments, success, error, loading } = useGet();

  useEffect(() => {
    if (success) {
      toast.success("Documents regenerated successfully");
      window.location.reload();
    }
  }, [success]);

  useEffect(() => {
    if (error && Object.keys(error).length > 0) {
      toast.error("Error regenerating documents");
    }
  }, [error]);

  return (
    <Tooltip title="Regenerate Demand Notice" arrow>
      <Button
        onClick={() =>
          regenerateDocuments({
            api: `${API_INVOICES}${item.id}/regenerate-documents/`,
          })
        }
        color="primary"
        variant="outlined"
        style={{ minWidth: 0 }}
      >
        {loading ? (
          "Regenerating"
        ) : (
          <div className="flex align-items-center gap-1 ">
            <ReloadOutline /> Regenerate
          </div>
        )}
      </Button>
    </Tooltip>
  );
};

export const useBillingInvoices = () => {
  const { authToken } = useAuth();

  const tableColumns = () => [
    createColumn("invoice_number", true),
    createColumn("car_number", true),
    createColumn("amount", true),
    createColumn("amount_paid", true, "Paid Amount"),
    createColumn("balance", true),
    createColumn("total_due", false),
    createColumn("type", true),
    createColumn("payment_status", false),
    createColumn("status", false, "Payment"),
    createColumn("actions", false),
  ];

  const downloadPDF = async (id: string) => {
    try {
      const response = await axios({
        method: "GET",
        url: `${BASE_API_URL}generate-invoice-qrcode-pdf/?invoice_id=${id}`,
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        responseType: "blob",
      });

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `qr_code_${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success("PDF downloaded successfully");
    } catch (error) {
      message.error("Error downloading PDF");
    }
  };

  const tableData = (data: any) => {
    if (!data || data?.results?.length === 0) return [];

    return data.map((item: any) => ({
      ...item,
      amount:
        item?.amount === 0.0 ? <span>0</span> : numberFormat(item?.amount),
      total_due:
        item?.total_due === 0.0 ? (
          <span>0</span>
        ) : (
          numberFormat(item?.total_due)
        ),
      exempted:
        item?.exempted === true ? (
          <span className="inline-block px-2 py-1 rounded-full text-xs font-medium text-green-600 bg-green-100">
            Yes
          </span>
        ) : (
          <span className="inline-block px-2 py-1 rounded-full text-xs font-medium text-yellow-600 bg-yellow-100">
            No
          </span>
        ),

      defaulted:
        item?.defaulted === true ? (
          <span className="inline-block px-2 py-1 rounded-full text-xs font-medium text-green-600 bg-green-100">
            Yes
          </span>
        ) : (
          <span className="inline-block px-2 py-1 rounded-full text-xs font-medium text-yellow-600 bg-yellow-100">
            No
          </span>
        ),

      status: (
        <div className="flex gap-2">
          {item?.payment_status !== "paid" &&
            item?.payment_status !== "PAID" &&
            item?.exempted !== true && (
              <Link
                className="link-with-icon"
                to={`/make_payments/create`}
                state={{ item }}
              >
                Make Payment
              </Link>
            )}
        </div>
      ),

      amount_paid:
        item?.amount_paid === 0.0 ? (
          <span>0</span>
        ) : (
          numberFormat(item?.amount_paid)
        ),
      balance:
        item?.balance === 0.0 ? <span>0</span> : numberFormat(item?.balance),
      number_of_coupons:
        item?.number_of_coupons === 0 ? <span>0</span> : item.number_of_coupons,
      payment_status: (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColorClass(item.payment_status)}`}
        >
          {formatPaymentStatus(item.payment_status)}
        </span>
      ),
      time_spent: formatTimeSpent(item.time_spent),
      type: item.type?.charAt(0).toUpperCase() + item.type?.slice(1) || "N/A",
      created_at: formatDateTime(item.created_at),
      created_by:
        item.created_by?.first_name && item.created_by?.last_name
          ? `${item.created_by.first_name} ${item.created_by.last_name}`
          : "System",
      car_number: item.car_number || "N/A",
      actions: (
        <FormActions
          actions={[
            createViewAction(URL_BILLING_INVOICES, item?.id),
            {
              label: "Print QR Code",
              custom: true,
              icon: PrintOutlined,
              modalTitle: "QR Code Payment",
              modalOkText: "Print QR Code",
              modalOkType: "primary" as const,
              modalCancelText: "Close",
              modalWidth: 600,
              modalContent: (
                <QRCodeModalComponent
                  invoiceId={item.id}
                  authToken={authToken}
                />
              ),
              onModalOk: async () => {
                await downloadPDF(item?.id);
              },
            },
          ]}
        />
      ),
    }));
  };

  const viewData = (item: any) => {
    // Handle case where item is undefined or null
    if (!item) {
      return {
        sections: [
          {
            title: "Error",
            fields: {
              message: "No invoice data available",
            },
          },
        ],
      };
    }

    return {
      sections: [
        {
          title: "Invoice Information",
          fields: {
            invoice_number: item?.invoice_number || "N/A",
            type:
              item?.type?.charAt(0).toUpperCase() + item?.type?.slice(1) ||
              "N/A",
            car_number: item?.car_number || "N/A",
            payment_status: formatPaymentStatus(item?.payment_status),
          },
        },
        {
          title: "Financial Details",
          fields: {
            amount: item?.amount || 0,
            amount_paid: item?.amount_paid || 0,
            balance: item?.balance || 0,
            defaulted: item?.defaulted ? "Yes" : "No",
          },
        },
        {
          title: "Parking Information",
          fields: {
            time_spent: formatTimeSpent(item?.time_spent),
            number_of_coupons: item?.number_of_coupons || 0,
            // declaration: item?.declaration ? "Accepted" : "Not accepted",
          },
        },
        {
          title: (
            <div
              style={{
                display: "flex",
                width: "100%",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 20,
              }}
            >
              Supporting Documents
              {item?.payment_status === "not_paid" && (
                <RegenerateDocuments item={item} />
              )}
            </div>
          ),
          visible:
            (item?.payment_status === "not_paid" && item?.type === "parking") ||
            item?.type === "Loading & Offloading",
          // fields: {
          //   ...docsList,
          // },
          fields: {
            Demand_Notice: (
              <Button
                color="primary"
                size="large"
                variant="outlined"
                onClick={() => {
                  const file =
                    item?.invoice_attachments?.demand_notice[0]?.file?.replace(
                      /^\//,
                      "",
                    );

                  const fileUrl = `${BASE_MEDIA_API_URL}${file}`;
                  toast.success("Downloading ...");
                  if (fileUrl) {
                    downloadThroughAnchorLink(fileUrl, file);
                  }
                }}
                disabled={!item?.invoice_attachments?.demand_notice}
              >
                Download Notice
              </Button>
            ),
          },
        },
        {
          title: "System Information",
          fields: {
            record_id: item?.id || "N/A",
            object_id: item?.object_id || "N/A",
            content_type: item?.content_type || "N/A",
            created_by:
              item?.created_by?.first_name && item?.created_by?.last_name
                ? `${item.created_by.first_name} ${item.created_by.last_name}`
                : "System",
            created_at: item?.created_at
              ? formatDateTime(item.created_at)
              : "N/A",
            updated_at: item?.updated_at
              ? formatDateTime(item.updated_at)
              : "N/A",
            updated_by:
              item?.updated_by?.first_name && item?.updated_by?.last_name
                ? `${item.updated_by.first_name} ${item.updated_by.last_name}`
                : "Not updated",
            deleted: item?.deleted ? "Yes" : "No",
            defaulted: item?.defaulted ? "Yes" : "No",
          },
        },
      ],
    };
  };

  const activityFilter = "invoices";

  // Calculate summary statistics
  const getSummaryStats = (data: any) => {
    if (!data || !data.results) return null;

    const invoices = data.results;
    const totalAmount = invoices.reduce(
      (sum: any, invoice: any) => sum + (invoice.amount || 0),
      0,
    );
    const totalPaid = invoices.reduce(
      (sum: any, invoice: any) => sum + (invoice.amount_paid || 0),
      0,
    );
    const totalBalance = invoices.reduce(
      (sum: any, invoice: any) => sum + (invoice.balance || 0),
      0,
    );
    const paidCount = invoices.filter(
      (invoice) => invoice.payment_status === "paid",
    ).length;
    const unpaidCount = invoices.filter(
      (invoice) => invoice.payment_status === "not_paid",
    ).length;

    return {
      totalAmount: totalAmount,
      totalPaid: totalPaid,
      totalBalance: totalBalance,
      totalInvoices: invoices.length,
      paidCount,
      unpaidCount,
      paidPercentage:
        invoices.length > 0
          ? Math.round((paidCount / invoices.length) * 100)
          : 0,
    };
  };

  return {
    tableData,
    tableColumns,
    viewData,
    activityFilter,
    getSummaryStats,
  };
};

export const useBillingInvoicesForm = () => {
  const fields = [
    {
      field_name: "car_number",
      label: "Car Number",
      type: "text",
      props: { required: true, placeholder: "e.g., UAG 622P" },
    },
    {
      field_name: "amount",
      label: "Invoice Amount",
      type: "number",
      props: { required: true, min: 0, step: "0.01", placeholder: "0.00" },
    },
    {
      field_name: "type",
      label: "Invoice Type",
      type: "select",
      props: { required: true },
      options: [
        { value: "parking", label: "Parking" },
        { value: "penalty", label: "Penalty" },
        { value: "service", label: "Service" },
      ],
    },
    {
      field_name: "payment_status",
      label: "Payment Status",
      type: "select",
      props: { required: true },
      options: [
        { value: "not_paid", label: "Not Paid" },
        { value: "paid", label: "Paid" },
        { value: "partially_paid", label: "Partially Paid" },
        { value: "overdue", label: "Overdue" },
      ],
    },
    {
      field_name: "amount_paid",
      label: "Amount Paid",
      type: "number",
      props: { required: false, min: 0, step: "0.01", placeholder: "0.00" },
    },
    {
      field_name: "time_spent",
      label: "Time Spent",
      type: "text",
      props: { required: false, placeholder: "HH:MM:SS" },
    },
    {
      field_name: "declaration",
      label: "Declaration Accepted",
      type: "checkbox",
      props: { required: false },
    },
    {
      field_name: "exempted",
      label: "Exempted",
      type: "checkbox",
      props: { required: false },
    },
  ];

  const sections = [
    {
      title: "Invoice Details",
      fields: fields.slice(0, 4), // car_number, amount, type, payment_status
    },
    {
      title: "Payment Information",
      fields: fields.slice(4, 6), // amount_paid, time_spent
    },
    {
      title: "Additional Options",
      fields: fields.slice(6), // declaration, exempted
    },
  ];

  const redirect = getRegister(URL_BILLING_INVOICES);

  const defaultValues = {};

  return { fields, sections, redirect, defaultValues };
};

export const BillingInvoicesForm = ({
  form,
  error,
  updateFormValue,
  onChange,
}) => {
  const { fields } = useBillingInvoicesForm();
  return (
    <FormFields
      fields={fields}
      error={error}
      form={form}
      updateFormValue={updateFormValue}
      onChange={onChange}
    />
  );
};

const QRCodeModalComponent = ({ invoiceId, authToken }) => {
  const [invoiceData, setInvoiceData] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchInvoiceData = async () => {
      try {
        const response = await axios({
          method: "GET",
          url: `${BASE_API_URL}${API_INVOICES}${invoiceId}/`,
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });
        setInvoiceData(response.data);
        setLoading(false);
      } catch (error) {
        message.error("Error loading QR code");
        setLoading(false);
      }
    };

    fetchInvoiceData();
  }, [invoiceId, authToken]);

  if (loading) {
    return (
      <QRCodeModalContent>
        <p>Loading QR Code...</p>
      </QRCodeModalContent>
    );
  }

  if (!invoiceData?.qr_code_url) {
    return (
      <QRCodeModalContent>
        <p>QR Code not available for this invoice</p>
      </QRCodeModalContent>
    );
  }

  return (
    <QRCodeModalContent>
      <h3>Scan QR Code to Pay</h3>
      <QRCodeImage src={invoiceData.qr_code_url} alt="QR Code" />
      <QRCodeData>
        <strong>Invoice:</strong> {invoiceData.invoice_number}
        <br />
        <strong>Amount:</strong>{" "}
        {formatAmount(Math.round(invoiceData.total_due || invoiceData.amount))}
        <br />
        <strong>Alternatively:</strong> {invoiceData.invoice_message}
      </QRCodeData>
    </QRCodeModalContent>
  );
};

// Styled Components for QR Code Modal
const QRCodeModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  text-align: center;

  h3 {
    margin-bottom: 20px;
    color: #262626;
    font-size: 18px;
  }

  p {
    color: #595959;
  }
`;

const QRCodeImage = styled.img`
  max-width: 300px;
  width: 100%;
  height: auto;
  margin: 20px 0;
  border: 2px solid #d9d9d9;
  border-radius: 8px;
  padding: 10px;
  background: white;
`;

const QRCodeData = styled.p`
  font-size: 12px;
  color: #8c8c8c;
  word-break: break-all;
  max-width: 500px;
  margin-top: 10px;
  padding: 10px;
  background: #f5f5f5;
  border-radius: 4px;
  text-align: left;
`;
